import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ImageIcon, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * OwnershipClaimsAdmin — Phase 3 admin tab.
 * Lists ownership claims, filters by status, and lets admins approve/reject
 * pending legacy-proof claims (with an inline private-image preview).
 */
export default function OwnershipClaimsAdmin() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    setRows(null);
    const r = await api.get("/admin/ownership-claims", {
      params: status ? { status } : {},
    });
    setRows(r.data.claims || []);
    setCounts(r.data.counts || {});
  };

  useEffect(() => { load(); }, [status]);

  const filters = [
    { key: "pending", label: "Pending", count: counts.pending },
    { key: "approved", label: "Approved", count: counts.approved },
    { key: "rejected", label: "Rejected", count: counts.rejected },
    { key: "cancelled", label: "Cancelled", count: counts.cancelled },
    { key: "revoked", label: "Revoked", count: counts.revoked },
    { key: "", label: "All" },
  ];

  return (
    <div data-testid="ownership-claims-admin">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="kicker kicker-red">Ownership claims</div>
        <div className="flex flex-wrap gap-1 p-1 rounded-2xl border-2 border-[var(--ink)] bg-white" data-testid="ownership-filters">
          {filters.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setStatus(f.key)}
              data-testid={`ownership-filter-${f.key || "all"}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-widest ${
                status === f.key
                  ? "bg-[var(--ink)] text-[var(--panini-yellow)]"
                  : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
              }`}
            >
              {f.label}{typeof f.count === "number" ? ` (${f.count})` : ""}
            </button>
          ))}
        </div>
      </div>

      {!rows ? (
        <div className="text-[var(--smoke)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[var(--smoke)] p-6 rounded-2xl border-2 border-dashed border-[var(--line)]" data-testid="ownership-empty">
          No claims match this filter.
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest bg-[var(--panini-yellow-soft)]">
              <tr>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Certificate</th>
                <th className="text-left px-4 py-3">Collector</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-[var(--line)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`ownership-row-${c.id}`}>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3 font-mono text-[var(--panini-red)] font-bold">{c.cert_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold">{c.collector_display_name || "—"}</div>
                    <div className="text-[11px] text-[var(--smoke)]">{c.collector_email || "—"}</div>
                    {c.public_collector_id && (
                      <div className="text-[11px] font-mono text-[var(--smoke)]">{c.public_collector_id}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--smoke)]">
                    {c.method === "claim_code" ? "Claim code" : "Legacy proof"}
                  </td>
                  <td className="px-4 py-3 text-[var(--smoke)] text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    {c.method === "claim_code" && c.status !== "approved" ? (
                      <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Auto</span>
                    ) : (
                      <button
                        onClick={() => setReviewing(c)}
                        data-testid={`ownership-open-${c.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--panini-yellow)] font-bold"
                      >
                        {c.status === "pending" ? "Review" : c.status === "approved" ? "Manage" : "View"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reviewing && (
        <ReviewModal
          claim={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ claim, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [revokeMode, setRevokeMode] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [imageErr, setImageErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    if (claim?.proof?.media_id) {
      api.get(`/media/${claim.proof.media_id}`, { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          blobUrl = URL.createObjectURL(r.data);
          setImageBlobUrl(blobUrl);
        })
        .catch(() => setImageErr(true));
    }
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [claim?.proof?.media_id]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/ownership-claims/${claim.id}/review`, {
        decision,
        review_notes: notes.trim() || null,
      });
      toast.success(decision === "approve" ? "Claim approved." : "Claim rejected.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const reason = revokeReason.trim();
    if (reason.length < 4) {
      toast.error("Please provide a reason (min 4 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/ownership-claims/${claim.id}/revoke`, { reason });
      toast.success("Ownership revoked.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const pending = claim.status === "pending";
  const approved = claim.status === "approved";

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="ownership-review-modal">
      <div className="w-full max-w-3xl bg-white border-2 border-[var(--ink)] rounded-2xl my-8 shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[var(--ink)]">
          <div>
            <div className="font-display text-2xl">Review claim</div>
            <div className="text-xs text-[var(--smoke)] font-mono mt-1">{claim.cert_number}</div>
          </div>
          <button onClick={onClose} data-testid="ownership-review-close" className="text-[var(--ink)]/70 hover:text-[var(--ink)]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl border-2 border-[var(--ink)] bg-[var(--ink)] overflow-hidden aspect-[4/3] flex items-center justify-center" data-testid="ownership-proof-image-wrap">
              {imageBlobUrl ? (
                <img src={imageBlobUrl} alt="Claim proof" className="max-h-full max-w-full object-contain" data-testid="ownership-proof-image" />
              ) : imageErr ? (
                <div className="text-white/70 text-sm text-center px-4">
                  <ImageIcon size={28} className="mx-auto mb-2 opacity-60" />
                  Image unavailable.
                </div>
              ) : (
                <div className="text-white/60 text-xs">Loading proof…</div>
              )}
            </div>
            <div className="text-sm space-y-3">
              <div>
                <div className="kicker kicker-black text-[10px] mb-1">Certificate</div>
                <div className="font-mono text-[var(--panini-red)] font-bold">{claim.cert_number}</div>
              </div>
              <div>
                <div className="kicker kicker-black text-[10px] mb-1">Collector</div>
                <div className="font-bold">{claim.collector_display_name || "—"}</div>
                <div className="text-xs text-[var(--smoke)]">{claim.collector_email}</div>
                {claim.public_collector_id && (
                  <div className="text-xs font-mono text-[var(--smoke)]">{claim.public_collector_id}</div>
                )}
              </div>
              <div>
                <div className="kicker kicker-black text-[10px] mb-1">Status</div>
                <StatusPill status={claim.status} />
              </div>
              <div>
                <div className="kicker kicker-black text-[10px] mb-1">Submitted</div>
                <div className="text-xs text-[var(--smoke)]">{new Date(claim.created_at).toLocaleString()}</div>
              </div>
              {claim.reviewed_at && (
                <div>
                  <div className="kicker kicker-black text-[10px] mb-1">Reviewed</div>
                  <div className="text-xs text-[var(--smoke)]">{new Date(claim.reviewed_at).toLocaleString()}</div>
                </div>
              )}
              {claim.review_notes && (
                <div>
                  <div className="kicker kicker-black text-[10px] mb-1">Prior reviewer notes</div>
                  <div className="text-sm bg-[var(--panini-yellow-soft)] rounded-lg px-3 py-2">{claim.review_notes}</div>
                </div>
              )}
            </div>
          </div>

          {pending && (
            <div>
              <div className="kicker kicker-black text-[11px] mb-2">Reviewer notes (optional, emailed to collector)</div>
              <textarea
                data-testid="ownership-review-notes"
                className="gm-field"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                placeholder="Why this is being approved or rejected…"
              />
            </div>
          )}

          {pending && (
            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={() => decide("approve")}
                disabled={busy}
                data-testid="ownership-approve"
                className="btn-red bg-[var(--field-green,#16a34a)] hover:bg-[var(--field-green-dark,#15803d)] border-[var(--ink)] flex-1 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                style={{ background: "#16a34a" }}
              >
                <CheckCircle2 size={16} /> Approve &amp; assign ownership
              </button>
              <button
                onClick={() => decide("reject")}
                disabled={busy}
                data-testid="ownership-reject"
                className="btn-ghost text-[var(--panini-red)] border-[var(--panini-red)] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <XCircle size={16} /> Reject
              </button>
            </div>
          )}

          {approved && !revokeMode && (
            <div className="pt-2 border-t border-[var(--line)]">
              <button
                onClick={() => setRevokeMode(true)}
                data-testid="ownership-revoke-open"
                className="btn-ghost text-[var(--panini-red)] border-[var(--panini-red)] text-sm inline-flex items-center gap-2"
              >
                <XCircle size={14} /> Revoke ownership
              </button>
              <p className="text-[11px] text-[var(--smoke)] mt-2">
                Destructive: clears the certificate&#39;s ownership and emails the former owner.
                Requires a written reason for the audit log.
              </p>
            </div>
          )}

          {approved && revokeMode && (
            <div className="pt-2 border-t border-[var(--line)] space-y-3">
              <div>
                <div className="kicker kicker-black text-[11px] mb-2">Reason for revocation (required, min 4 chars — emailed to collector)</div>
                <textarea
                  data-testid="ownership-revoke-reason"
                  className="gm-field"
                  rows={3}
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  maxLength={2000}
                  placeholder="Why is this ownership being revoked?"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={revoke}
                  disabled={busy}
                  data-testid="ownership-revoke-confirm"
                  className="btn-red disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  <XCircle size={16} /> Confirm revocation
                </button>
                <button
                  onClick={() => { setRevokeMode(false); setRevokeReason(""); }}
                  disabled={busy}
                  data-testid="ownership-revoke-cancel"
                  className="btn-ghost text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: { bg: "#dcfce7", fg: "#166534", Icon: CheckCircle2, label: "Approved" },
    pending:  { bg: "#fef3c7", fg: "#92400e", Icon: Clock,        label: "Pending" },
    rejected: { bg: "#fee2e2", fg: "#991b1b", Icon: XCircle,      label: "Rejected" },
    cancelled:{ bg: "#e5e7eb", fg: "#374151", Icon: X,            label: "Cancelled" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
      style={{ background: cfg.bg, color: cfg.fg }}
    >
      <Icon size={11} /> {cfg.label}
    </span>
  );
}
